import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import {
  DAY_VIEW_LIST,
  registerDropList,
  syncDraggables,
} from "../../../utils/collapse-drag-controller";
import {
  buildSupersets,
  computeSectionPrefixes,
  groupExercisesBySection,
  groupOptimisticMove,
  reorderPreservingSupersets,
  rowsFromDraggables,
  sectionOrdersForIds,
  supersetChains,
} from "../../../utils/prefix-calculator";
import { splitSections } from "../../../utils/section-types";
import SectionSeparator from "../../athlete-portal/calendar/components/EditMode/ExerciseEditor/SectionSeparator";
import WorkoutNoteBlock from "../../athlete-portal/calendar/components/EditMode/ExerciseEditor/WorkoutNoteBlock";
import EditorSessionTableRow from "../../dashboard/program-builder/components/EditorSessionTableRow";
import DayExerciseLineItem from "./components/DayExerciseLineItem";

const ExerciseFrame = ({
  activeExercises,
  openQuickEdit,
  editCycle,
  program,
  session,
  fromMetric,
  activeUser,
  handleDuplicate,
  openQuickEditMode,
  handleQuickEditChange,
  handleAddSetLocally,
  loadInputs,
  intensityInputs,
  query,
  saveQuickEdit,
  exercises,
  closeQuickEditMode,
  ungroupedExercises,
  sections = [],
  // Create flow for the Day view's "+ Instructions" header button.
  creatingWorkoutNote = false,
  workoutNoteCreateBody,
  onCancelCreateWorkoutNote,
}) => {
  const { t } = useTranslation(["dashboard", "common"]);

  const listRef = useRef(null);
  const draggablesRef = useRef(new Map());
  const dropRef = useRef(null);
  const blocksRef = useRef(null);
  blocksRef.current = () =>
    buildSupersets(ungroupedExercises || activeExercises || []);
  dropRef.current = ({ item, from, oldIndex, newIndex, slotNode }) => {
    const exercises = ungroupedExercises || activeExercises || [];
    const rows = Array.from(from.querySelectorAll(DAY_VIEW_LIST.rows));
    // Same placement the calendar edit mode gives a node dropped before a row:
    // after the separator that leads it, ahead of the row itself.
    const rowIds = rows.map((row) => row.id);
    const [movedId] = rowIds.splice(oldIndex, 1);
    rowIds.splice(newIndex > oldIndex ? newIndex - 1 : newIndex, 0, movedId);

    // A complex is one row but several exercises; the API orders exercises.
    const orderedIds = rowIds.flatMap((id) => {
      const exercise = exercises.find((ex) => ex._id === id);
      if (!exercise?.complex || !exercise?.complexInstance) return [id];
      return exercises
        .filter(
          (ex) =>
            ex.complex === exercise.complex &&
            ex.complexInstance === exercise.complexInstance
        )
        .sort((a, b) => a.indexInComplex - b.indexInComplex)
        .map((ex) => ex._id);
    });
    const orderedFrom = reorderPreservingSupersets(
      exercises,
      orderedIds,
      item.id
    );

    // Separators keep their place in the DOM; only the moved row jumps.
    const layout = Array.from(
      from.querySelectorAll(
        `${DAY_VIEW_LIST.rows}, :scope > .section-separator`
      )
    ).filter((node) => node !== item);
    // A slot above a header ends the group before it; below, it starts the next.
    const before = slotNode === undefined ? rows[newIndex] : slotNode;
    const at = before ? layout.indexOf(before) : layout.length;
    layout.splice(at, 0, item);

    submit(
      `/actions/exercise/workout-reorder?proxyAthlete=${query?.athlete}&reqSource=calendar`,
      {
        method: "POST",
        body: {
          exercises: orderedFrom,
          // A complex is one row for several exercises, so count by id.
          sections: sectionOrdersForIds(
            rowsFromDraggables(layout),
            orderedFrom
          ),
          supersets: supersetChains(exercises, orderedFrom),
        },
      }
    );

    // Show the dragged group where the save will put it.
    return groupOptimisticMove(exercises, item.id, orderedFrom);
  };
  const hasExercises = !!activeExercises;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the list node only exists once exercises have loaded.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    return registerDropList(
      list,
      (event) => dropRef.current(event),
      DAY_VIEW_LIST,
      () => blocksRef.current()
    );
  }, [hasExercises]);
  useEffect(() => {
    if (listRef.current) syncDraggables(listRef.current, draggablesRef.current);
  });
  useEffect(
    () => () => {
      for (const entry of draggablesRef.current.values()) entry.destroy();
      draggablesRef.current.clear();
    },
    []
  );

  // Section overlay: per-section letter reset + read-only separators. Computed
  // from the ungrouped exercises (which carry order) so membership is correct.
  const exercisesForSections = ungroupedExercises || activeExercises || [];
  // groupExercisesBySection/computeSectionPrefixes below already exclude the
  // workout note internally; pulled out here only so it can be rendered.
  const { note } = splitSections(sections);
  const sectionPrefixes = computeSectionPrefixes(
    exercisesForSections,
    sections
  );
  const sectionByFirstExerciseId = {};
  // A section name is locked once any of its exercises has been completed —
  // same rule as a single exercise (sets with outcomes), see ExerciseEditor.
  const sectionLockedById = {};
  const emptySections = [];
  groupExercisesBySection(exercisesForSections, sections).forEach((group) => {
    if (!group.section) return;
    sectionLockedById[group.section._id] = group.exercises.some((ex) =>
      ex.sets?.some((set) => set.outcomes)
    );
    if (group.exercises.length) {
      sectionByFirstExerciseId[group.exercises[0]._id] = group.section;
    } else {
      emptySections.push(group.section);
    }
  });

  // Per-section last membership over the rendered (complex-grouped) rows, used
  // to keep a superset/complex from being started across a section boundary
  // (see EditorSessionTableRow's create-superset button). Move up/down are
  // intentionally not restricted here: section membership is order-derived
  // (see groupExercisesBySection), so moving an exercise past a section
  // separator simply reassigns it to the adjacent section.
  const sectionLastRowId = {};
  groupExercisesBySection(activeExercises, sections).forEach((group) => {
    if (!group.exercises.length) return;
    sectionLastRowId[group.exercises[group.exercises.length - 1]._id] = true;
  });

  return activeExercises ? (
    <section className={`exercises-frame ${openQuickEdit && "quick-edit"}`}>
      <form id="quick-edit">
        <ul className="exercises-list" ref={listRef}>
          <WorkoutNoteBlock
            key={`${workoutNoteCreateBody?.utcDate}-${note?._id ?? "new"}`}
            note={note}
            athleteId={query?.athlete}
            reqSource="calendar"
            creating={creatingWorkoutNote}
            createBody={workoutNoteCreateBody}
            onCancelCreate={onCancelCreateWorkoutNote}
          />
          {activeExercises.map((exercise, i) => (
            <React.Fragment key={`${exercise._id}tablerow`}>
              {sectionByFirstExerciseId[exercise._id] && (
                <SectionSeparator
                  section={sectionByFirstExerciseId[exercise._id]}
                  editable={false}
                  nameEditable={
                    !sectionLockedById[
                      sectionByFirstExerciseId[exercise._id]._id
                    ]
                  }
                  athleteId={query?.athlete}
                  reqSource="calendar"
                  autoEdit={
                    sectionByFirstExerciseId[exercise._id]._id ===
                    query?.editSection
                  }
                />
              )}
              <EditorSessionTableRow
                exercises={activeExercises}
                ungroupedExercises={ungroupedExercises}
                exercise={exercise._id}
                exerciseOrder={i}
                sectionPrefix={sectionPrefixes[exercise._id]}
                isSectionLast={!!sectionLastRowId[exercise._id]}
                cycle={editCycle}
                program={program}
                programOrder={program ? program.order : {}}
                session={session}
                fromMetric={fromMetric}
                activeUser={activeUser}
                handleDuplicate={handleDuplicate}
                openQuickEditMode={openQuickEditMode}
                handleQuickEditChange={handleQuickEditChange}
                handleAddSetLocally={handleAddSetLocally}
                loadInputs={loadInputs}
                intensityInputs={intensityInputs}
                isQuickEditOpen={openQuickEdit}
                LineItemComponent={DayExerciseLineItem}
              />
            </React.Fragment>
          ))}
          {emptySections.map((section) => (
            <SectionSeparator
              key={section._id}
              section={section}
              editable={false}
              nameEditable={!sectionLockedById[section._id]}
              athleteId={query?.athlete}
              reqSource="calendar"
              autoEdit={section._id === query?.editSection}
            />
          ))}
        </ul>

        <footer>
          <button
            onClick={saveQuickEdit}
            data-button-type="primary"
            disabled={!exercises.length && !query.edit}
            type="button"
          >
            {t("common:save")}
          </button>
          <button
            onClick={closeQuickEditMode}
            data-button-type="secondary"
            className="text-(--danger)!"
            type="button"
          >
            {t("calendar.exerciseFrame.discard")}
          </button>
        </footer>
      </form>
    </section>
  ) : (
    <h2>{t("calendar.exerciseFrame.workoutNotExist")}</h2>
  );
};

export default ExerciseFrame;
